import React, { useEffect, useRef, useState } from "react";
import OtpInput from "react-otp-input";
import ReactModal from "react-modal";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import ReCAPTCHAComponent from "react-google-recaptcha";

import { ConfirmButton } from "@/components/button/confirmButton";
import { submitRecaptchaData, getPhoneConfirmationOtp } from "@/dataLayer";
import { ProfileIcon } from "@/components/_icons/profileIcon";
import { TextButton } from "@/components/button/textButton";
import { SendCodeIcon } from "@/components/_icons/sendCodeIcon";
import LocalizedPhoneInput from "@/components/_localized/localizedPhoneInput";
import { localeOptions } from "@/components/_localized/localizationData";
import { Merchant } from "@/dto/merchant";

export const LoginInfoModal = ({
  isOpen,
  onRequestClose,
  merchantColor,
  toCheckoutAfterLogin,
  merchant,
}: {
  merchant: Merchant;
  isOpen: boolean;
  onRequestClose: () => void;
  merchantColor: string;
  toCheckoutAfterLogin: boolean;
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const router = useRouter();
  const [showVerification, setShowVerification] = useState(false);
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const captchaRef = useRef<ReCAPTCHAComponent | null>(null);
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";
  const serviceCart = JSON.parse(localStorage.getItem("serviceCart") || "[]");
  const showGuestCheckout =
    (merchant.isRetail || merchant.isRestaurant || serviceCart.length === 0) &&
    toCheckoutAfterLogin;
  const phoneField = "phone";

  useEffect(() => {
    if (code.length === 6) {
      confirmCode();
    }
  }, [code]);

  useEffect(() => {
    if (isOpen && toCheckoutAfterLogin) {
      router.prefetch("/checkout");
    }
  }, [isOpen, toCheckoutAfterLogin]);

  const form = useForm({
    defaultValues: {
      phone: "",
    },
    mode: "all",
  });

  const {
    formState: { isValid },
    getValues,
    setValue,
  } = form;

  async function onRecaptchaSubmit(value: any) {
    if (!value) {
      return;
    }
    let formattedPhoneNumber = getValues(phoneField).replace(/\D/g, "");
    formattedPhoneNumber =
      merchant.locale === "en-AU"
        ? formattedPhoneNumber.replace(/^0+/, "")
        : formattedPhoneNumber;
    formattedPhoneNumber = `${
      localeOptions[merchant.locale]?.countryCode
    }${formattedPhoneNumber}`;
    let recaptcha = value;
    const result = await submitRecaptchaData(
      formattedPhoneNumber,
      "CWO_WEB",
      recaptcha
    );
    if (result.status === 204) {
      setShowVerification(true);
      setErrorMessage(null);
    } else {
      setErrorMessage(
        result.data.message || "An error occurred with the captcha."
      );
    }
  }

  const confirmCode = async () => {
    let formattedPhoneNumber = getValues(phoneField).replace(/\D/g, "");
    formattedPhoneNumber =
      merchant.locale === "en-AU"
        ? formattedPhoneNumber.replace(/^0+/, "")
        : formattedPhoneNumber;
    formattedPhoneNumber = `${
      localeOptions[merchant.locale]?.countryCode
    }${formattedPhoneNumber}`;

    const result = await getPhoneConfirmationOtp(
      formattedPhoneNumber,
      "CWO_WEB",
      code
    );
    if (
      result?.globalCustomerUuid &&
      result?.refreshToken &&
      result?.sessionToken
    ) {
      setCode("");
      if (toCheckoutAfterLogin) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        router.push("/checkout");
      } else {
        setShowVerification(false);
        onRequestClose();
      }
    } else if (result.status && result.status.code === 403) {
      setErrorMessage(
        result.message || "There is an error with your OTP code."
      );
    } else {
      setErrorMessage("Unexpected error occurred. Please try again.");
    }
  };

  const handleGuestCheckout = () => {
    if (toCheckoutAfterLogin) {
      router.push("/guest-checkout");
    } else {
      onRequestClose();
    }
  };

  return (
    <ReactModal
      ariaHideApp={false}
      appElement={global?.window?.document?.getElementById("app") || undefined}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="fixed inset-0 flex items-center justify-center"
      overlayClassName="fixed inset-0 bg-black bg-opacity-75 z-50"
    >
      <ReCAPTCHAComponent
        className=""
        sitekey={recaptchaKey}
        ref={captchaRef}
        onChange={onRecaptchaSubmit}
        size="invisible"
        // asyncScriptOnLoad={asyncScriptOnLoad}
        // onErrored={handleRecaptchaError}
        badge="bottomright"
      />
      {!showVerification ? (
        <div className="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-[auto] sm:w-[70%] mx-auto overflow-hidden p-4 [@media(max-height:480px)]:h-[100%] [@media(max-height:480px)]:overflow-auto">
          <div className="flex justify-between items-center px-6 pt-6">
            <button
              onClick={() => {
                setValue(phoneField, "");
                setErrorMessage(null);
                onRequestClose();
              }}
              className="text-gray-600 hover:text-gray-900 ml-auto"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="px-4 pb-4 flex flex-col justify-center items-center">
            <ProfileIcon brandingColor={merchantColor} />
            <p className="mt-4 text-3xl font-medium text-center text-neutral-700">
              Sign up or log in
            </p>
            <p className="mt-4 text-base leading-6 text-center text-[15px] text-neutral-700 sm:w-[460px]">
              Check out faster and earn perks for your orders with a Clover
              account.
            </p>
            <div className="my-5 w-full sm:w-1/2 flex flex-col items-center">
              {/* TODO: ADD NEGATIVE FLOWS TO THESE INPUTS */}
              <LocalizedPhoneInput
                name="phone"
                form={form}
                locale={merchant.locale}
              />
            </div>
            {errorMessage && (
              <div className="text-red-600 text-sm mt-2">{errorMessage}</div>
            )}
            <ConfirmButton
              disabled={!isValid}
              style={{ backgroundColor: !isValid ? "#C7C7C7" : merchantColor }}
              className="px-16 py-5 mt-11 max-w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap bg-[#228800] disabled:bg-[#C7C7C7] rounded-lg w-[250px] max-md:px-5 max-md:mt-10"
              onClick={() => {
                captchaRef.current?.reset();
                captchaRef.current?.execute();
              }}
            >
              <p>Continue</p>
            </ConfirmButton>
            {showGuestCheckout && (
              <TextButton
                className="mt-8 mb-6 text-xl font-medium leading-7 text-center max-md:mt-10"
                onClick={handleGuestCheckout}
                style={{ color: merchantColor }}
              >
                Check out as a guest
              </TextButton>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-[auto] sm:w-[70%] mx-auto overflow-hidden">
          <div className="flex justify-between items-center px-6 pt-6">
            <button
              onClick={() => {
                setErrorMessage(null);
                setShowVerification(false);
              }}
              className="text-gray-600 hover:text-gray-900 mr-auto"
            >
              <svg
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.6992 7.875H4.97422L11.2742 1.575L9.69922 0L0.699219 9L9.69922 18L11.2742 16.425L4.97422 10.125H18.6992V7.875Z"
                  fill="#373737"
                />
              </svg>
            </button>
            <button
              onClick={() => {
                setValue(phoneField, "");
                onRequestClose();
                setShowVerification(false);
                setCode("");
              }}
              className="text-gray-600 hover:text-gray-900 ml-auto"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="px-4 pb-4 flex flex-col justify-center items-center">
            {/* <Image
              src={loginVerificationTopIcon.src}
              height={54}
              width={66}
              alt="trophy with shiny background"
            /> */}
            <SendCodeIcon brandingColor={merchantColor} />
            <p className="mt-4 text-3xl font-medium text-center text-neutral-700">
              Verify your phone number
            </p>
            <p className="mt-10  text-base leading-6 text-center text-[15px] text-neutral-700 sm:w-[460px]">
              A 6-digit code was just sent to{" "}
              <span className="font-bold">{getValues().phone}</span>. Please
              enter it below to keep your account information safe.
            </p>
            <div>
              <OtpInput
                numInputs={6}
                value={code}
                onChange={setCode}
                containerStyle={
                  "w-full px-2 py-3 focus:outline-none bg-transparent"
                }
                inputStyle={
                  "border-0 border-b-2 border-gray-disabled rounded-none font-base font-bold text-2xl h-100 m-0 mx-1.25 p-0 w-10 sm:border-b-4 sm:text-5xl sm:m-0 sm:mx-2 sm:w-21.5"
                }
                focusStyle={{ borderColor: merchantColor, outline: "none" }}
                errorStyle={"border-0 outline-none"}
                isInputNum={true}
                shouldAutoFocus={true}
              />
            </div>
            {errorMessage && code.length === 6 && (
              <div className="text-red-600 text-sm mt-2">{errorMessage}</div>
            )}
            <TextButton
              style={{ color: merchantColor }}
              className="mt-8 mb-6 text-xl font-medium leading-7 text-center text-[#228800] max-md:mt-10"
              onClick={() => {
                setCode("");
                setErrorMessage(null);
                captchaRef.current?.reset();
                captchaRef.current?.execute();
              }}
            >
              Re-send Code
            </TextButton>
          </div>
        </div>
      )}
    </ReactModal>
  );
};
